import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { GridApi, SortChangedEvent } from 'ag-grid-community';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { EmailManagementSettings } from '@anduin/api';
import { ConfigService, PmSystem } from '@anduin/config';
import { EmailDeliveryStep, EmailDeliveryTrackService } from '../../tracking';
import {
  applicationDebounceTime,
  areArraysEqual,
  DateConstants,
  getControlValueChanges,
  LoadableHelper,
  LoadableStatus,
  Logger,
  Range,
  SharedState,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { TOpens } from 'ng2-date-picker/lib/common/types/poistions.type';
import { IDatePickerDirectiveConfig } from 'ng2-date-picker/public-api';
import { distinctUntilChanged, filter, map, pairwise, tap } from 'rxjs';
import {
  EMAIL_TYPE_GROUP_LABELS,
  EMAIL_TYPE_GROUPS,
  EmailDeliveryHubFailedFilters,
  EmailDeliveryHubFilters, EmailDeliveryHubPendingFilters,
  EmailDeliveryHubSentFilters,
  EmailDeliveryHubUnsentFilters,
  getEnabledGroupLabels,
} from '../../models/email-delivery-hub-filters.models';
import { EmailDeliveryTab } from '../../models/email-delivery-tab.enum';
import { ToolbarDensity } from '../../containers/email-delivery-hub-container/responsive-toolbar.directive';
import { KEY_VALUE_STORAGE } from '../../services';
import { EmailDeliveryHubState, LoadPmsSyncStatus, UpdateFailedFilters, UpdateSentFilters, UpdatePendingFilters } from '../../state';
import { PmsSyncPollingState } from '../../state';
import { ALL_EMAIL_TYPES_LABEL } from '../../utils/all-email-types';
import { DEFAULT_EMAIL_DELIVERY_HUB_DATE_RANGE } from '../../utils/default-date-range';
import { FAILED_FILTERS_KEY, PENDING_FILTERS_KEY, SENT_FILTERS_KEY, SESSION_FILTERS_KEY } from '../../utils/session-filters-key';
import { dateRangeValidator } from './validators/date-range.validator';
import { skip } from 'rxjs/operators';

// Map for email types that existed in prior versions of the filter but were removed
// from EMAIL_TYPE_DEFINITIONS. Keeps saved session-storage filter preferences intact
// by routing them to their logical group instead of silently falling back to "All".
const LEGACY_EMAIL_TYPE_GROUP_MAP: Record<string, string> = {
  'Dynamic Smart Statement': EMAIL_TYPE_GROUP_LABELS.COLLECTIONS,
  'Smart Statement By Client': EMAIL_TYPE_GROUP_LABELS.COLLECTIONS,
  'Smart Statement By Contact': EMAIL_TYPE_GROUP_LABELS.COLLECTIONS,
  'Engagement Billing Schedule Confirmation': EMAIL_TYPE_GROUP_LABELS.BILLING,
  'Upcoming Payment': EMAIL_TYPE_GROUP_LABELS.PAYMENTS,
};

export function migrateLegacyEmailTypeFilters(types: string[]): string[] {
  const groupLabels = new Set(Object.keys(EMAIL_TYPE_GROUPS));
  const validOptions = new Set([ALL_EMAIL_TYPES_LABEL, ...groupLabels]);

  if (types.every((t) => validOptions.has(t))) {
    return types;
  }

  const resultGroups = new Set<string>();
  for (const type of types) {
    if (validOptions.has(type)) {
      resultGroups.add(type);
      continue;
    }
    const legacyGroup = LEGACY_EMAIL_TYPE_GROUP_MAP[type];
    if (legacyGroup) {
      resultGroups.add(legacyGroup);
      continue;
    }
    for (const [groupLabel, groupTypes] of Object.entries(EMAIL_TYPE_GROUPS)) {
      if (groupTypes.includes(type)) {
        resultGroups.add(groupLabel);
        break;
      }
    }
  }

  return resultGroups.size > 0 ? [...resultGroups] : [ALL_EMAIL_TYPES_LABEL];
}

@Component({
  selector: 'app-email-delivery-hub-filters',
  templateUrl: `./email-delivery-hub-filters.component.html`,
  styleUrl: './email-delivery-hub-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailDeliveryHubFiltersComponent implements OnInit {
  datePipe = inject(DatePipe);
  store = inject(Store);

  private readonly _initializedTabs = new Set<EmailDeliveryTab>();

  private readonly keyValueStorage = inject(KEY_VALUE_STORAGE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);
  private readonly trackService = inject(EmailDeliveryTrackService);

  readonly activeTab = input<EmailDeliveryTab>(EmailDeliveryTab.SENT);
  readonly density = input<ToolbarDensity>(0);

  readonly isSentTab = computed(() => this.activeTab() === EmailDeliveryTab.SENT);
  readonly isNonSentTab = computed(() => !this.isSentTab());

  readonly showPmsSyncStatus = computed(() => this.isNonSentTab() && this.configService.pmSystem === PmSystem.CCH_AXCESS);

  private readonly TABLE_NAMES: Record<EmailDeliveryTab, string> = {
    [EmailDeliveryTab.SENT]: 'emailDeliveryHubSent',
    [EmailDeliveryTab.PENDING]: 'emailDeliveryHubPending',
    [EmailDeliveryTab.FAILED]: 'emailDeliveryHubFailed',
  };

  private readonly STORAGE_KEYS: Record<EmailDeliveryTab, string> = {
    [EmailDeliveryTab.SENT]: SENT_FILTERS_KEY,
    [EmailDeliveryTab.PENDING]: PENDING_FILTERS_KEY,
    [EmailDeliveryTab.FAILED]: FAILED_FILTERS_KEY,
  };

  private get storageKey(): string {
    return this.STORAGE_KEYS[this.activeTab()];
  }

  readonly pmsSyncStatus = this.store.selectSignal(EmailDeliveryHubState.getPmsSyncStatus);
  readonly pmsSyncPollingState = this.store.selectSignal(EmailDeliveryHubState.getPmsSyncPollingState);
  readonly formattedLastSyncedAt = computed(() => {
    const lastSyncedAt = this.pmsSyncStatus()?.data?.lastSyncedAt;
    if (!lastSyncedAt) return '';
    return this.formatSyncDateTime(lastSyncedAt);
  });
  readonly pmsSyncPollingTooltip = computed(() => {
    const formatted = this.formattedLastSyncedAt();
    if (!formatted) return '';
    return 'Last synced at ' + formatted;
  });
  readonly LoadableStatus = LoadableStatus;
  readonly PmsSyncPollingState = PmsSyncPollingState;

  private readonly _enabledGroupLabels = getEnabledGroupLabels(this.configService);
  private readonly _autoCompleteEmailTypeOptions = [
    ALL_EMAIL_TYPES_LABEL,
    ...[...this._enabledGroupLabels].sort((a, b) => {
      if (a === EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS) return 1;
      if (b === EMAIL_TYPE_GROUP_LABELS.MISCELLANEOUS) return -1;
      return a.localeCompare(b);
    }),
  ];

  emailTypeOptions = signal(this._autoCompleteEmailTypeOptions);

  clientCommunicationHubDateRangeMinPickerConfig = this.getDateRangePickerConfig('left');
  clientCommunicationHubDateRangeMaxPickerConfig = this.getDateRangePickerConfig('right');

  emailDeliveryHubFiltersGroup = new FormGroup(
    {
      min: new FormControl<string | undefined>(undefined, { updateOn: 'blur' }),
      max: new FormControl<string | undefined>(undefined, { updateOn: 'blur' }),
      types: new FormControl([ALL_EMAIL_TYPES_LABEL], { updateOn: 'change' }),
      search: new FormControl('', { updateOn: 'change' }),
      showBouncedOnly: new FormControl(false, { updateOn: 'change' }),
      showDismissedAndResolved: new FormControl(false, { updateOn: 'change' }),
    },
    { validators: [dateRangeValidator] },
  );

  autoCompleteControl = this.emailDeliveryHubFiltersGroup.get('types');

  readonly gridApi = input<GridApi | undefined>();
  readonly hasActiveColumnFilters = signal(false);
  readonly hasActiveColumnFiltersChange = output<boolean>();

  lastRefresh = this.store.selectSignal(EmailDeliveryHubState.getLastRefresh);
  bouncedCount = this.store.selectSignal(EmailDeliveryHubState.getBouncedCount);

  formattedLastRefresh = toSignal(
    toObservable(this.lastRefresh).pipe(
      filter((lastRefresh) => LoadableHelper.isLoaded(lastRefresh?.status, true)),
      filter((lastRefresh) => !!lastRefresh.data?.completedAt || !!lastRefresh.data?.lastSuccessAt),
      map((lastRefresh) => {
        const { lastSuccessAt, completedAt, success } = lastRefresh.data;
        // A failed attempt's completedAt is when the attempt failed, not when data was last updated — never show it.
        const timestamp = lastSuccessAt ?? (success === false ? null : completedAt);
        return timestamp ? this.formatSyncDateTime(new Date(timestamp).getTime()) : null;
      }),
    ),
    { initialValue: '...' },
  );

  lastRefreshError = computed(() => {
    const lastRefresh = this.lastRefresh();
    return (
      lastRefresh?.status === LoadableStatus.ERROR || (lastRefresh?.status === LoadableStatus.LOADED && lastRefresh.data?.success === false)
    );
  });

  settings = this.store.selectSignal(EmailDeliveryHubState.getSettingsLoadable);

  nextUpdateTooltip = computed(() => {
    const lastRefresh = this.lastRefresh();
    const settings = this.settings();

    if (!LoadableHelper.isLoaded(lastRefresh?.status, true) || !LoadableHelper.isLoaded(settings?.status, true)) {
      return '';
    }

    const triggeredAt = lastRefresh.data?.triggeredAt;
    const maxTimeBetweenRefreshes = (settings.data as EmailManagementSettings)?.maxTimeBetweenRefreshes;

    if (!triggeredAt || !maxTimeBetweenRefreshes) {
      return '';
    }

    const nextUpdateTime = new Date(new Date(triggeredAt).getTime() + maxTimeBetweenRefreshes);
    const formattedTime = nextUpdateTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `Next update at ~${formattedTime}`;
  });

  constructor() {
    effect(() => {
      const tab = this.activeTab();

      // Immediately determine column-filter visibility from persisted grid state
      // so the button doesn't linger from the previous tab while the new grid loads.
      const tableName = this.TABLE_NAMES[tab];
      const savedGridState = this.store.selectSnapshot(SharedState.getAgGridState(tableName));
      const hasFilters = savedGridState?.filterModel != null && Object.keys(savedGridState.filterModel).length > 0;
      this.hasActiveColumnFilters.set(hasFilters);
      this.hasActiveColumnFiltersChange.emit(hasFilters);

      this._tryToFillFiltersFromSession(tab);
    });

    effect(() => {
      if (this.showPmsSyncStatus()) {
        this.store.dispatch(new LoadPmsSyncStatus());
      }
    });

    effect((onCleanup) => {
      const api = this.gridApi();
      if (!api) {
        this.hasActiveColumnFilters.set(false);
        return;
      }

      // Filter/sort events fire during state-restore before first data render; only emit
      // tracking once the user has had a chance to interact with the rendered grid.
      let trackingArmed = false;
      let lastSortState: Record<string, 'asc' | 'desc' | null> = {};
      const armTracking = () => {
        trackingArmed = true;
      };

      const updateFilterState = () => {
        const model = api.getFilterModel();
        const hasFilters = model != null && Object.keys(model).length > 0;
        this.hasActiveColumnFilters.set(hasFilters);
        this.hasActiveColumnFiltersChange.emit(hasFilters);
        this.trackService.setColumnFilters(model);
      };

      const trackFilterChanged = (event: { columns?: { getColId(): string }[] }) => {
        if (!trackingArmed) return;
        const model = api.getFilterModel() ?? {};
        const changed = event.columns?.length ? event.columns : Object.keys(model).map((id) => ({ getColId: () => id }));
        for (const col of changed) {
          const columnId = col.getColId();
          if (!columnId) continue;
          this.trackService.trackWorkflow(EmailDeliveryStep.COLUMN_FILTER_CHANGED, { columnId });
        }
      };

      const trackSortChanged = (event: SortChangedEvent) => {
        const currentSortState: Record<string, 'asc' | 'desc' | null> = {};
        for (const col of event.columnApi.getColumnState()) {
          if (!col.colId) continue;
          currentSortState[col.colId] = (col.sort === 'asc' || col.sort === 'desc') ? col.sort : null;
        }

        if (trackingArmed) {
          const columnIds = new Set([...Object.keys(lastSortState), ...Object.keys(currentSortState)]);
          for (const columnId of columnIds) {
            const previous = lastSortState[columnId] ?? null;
            const current = currentSortState[columnId] ?? null;
            if (previous === current || current == null) continue;
            this.trackService.trackWorkflow(EmailDeliveryStep.SORT_CHANGED, {
              columnId,
              direction: current,
            });
          }
        }

        lastSortState = currentSortState;
      };

      updateFilterState();
      api.addEventListener('firstDataRendered', armTracking);
      api.addEventListener('filterChanged', updateFilterState);
      api.addEventListener('filterChanged', trackFilterChanged);
      api.addEventListener('sortChanged', trackSortChanged);

      onCleanup(() => {
        api.removeEventListener('firstDataRendered', armTracking);
        api.removeEventListener('filterChanged', updateFilterState);
        api.removeEventListener('filterChanged', trackFilterChanged);
        api.removeEventListener('sortChanged', trackSortChanged);
      });
    });
  }

  ngOnInit(): void {
    getControlValueChanges(this.autoCompleteControl)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(areArraysEqual),
        pairwise(),
        tap(([previous, current]) => this._handleAllTypesAndIndividualToggle(current, previous)),
      )
      .subscribe();

    this.emailDeliveryHubFiltersGroup.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.emailDeliveryHubFiltersGroup.valid),
        applicationDebounceTime(),
        skip(1), // skip the initial emission on form initialization
        map(({ min, max, showBouncedOnly, showDismissedAndResolved, ...value }) => ({
          ...value,
          dateRange: { min: min, max: max },
          ...(this.isSentTab() ? { showBouncedOnly } : {}),
          ...(this.isNonSentTab() ? { showDismissedAndResolved } : {}),
        }) as EmailDeliveryHubFilters),
        tap((data) => {
          if (this.isSentTab()) {
            this.store.dispatch(new UpdateSentFilters(data as EmailDeliveryHubSentFilters));
          } else {
            if (this.activeTab() === EmailDeliveryTab.PENDING) {
              this.store.dispatch(new UpdatePendingFilters(data as EmailDeliveryHubPendingFilters));
            } else {
              this.store.dispatch(new UpdateFailedFilters(data as EmailDeliveryHubFailedFilters));
            }
          }
        }),
      )
      .subscribe();

    getControlValueChanges<string>(this.emailDeliveryHubFiltersGroup.get('search') as FormControl<string>)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        applicationDebounceTime(),
        map((value) => (value ?? '').trim()),
        distinctUntilChanged(),
        skip(1),
        tap((value) => {
          this.trackService.setSearchValue(value);
          this.trackService.trackWorkflow(EmailDeliveryStep.SEARCHED, { queryLength: value.length });
        }),
      )
      .subscribe();
  }

  /**
   * Attempts to restore filter values from session storage and initialize the form.
   *
   * This method retrieves previously saved filter values from session storage and applies them
   * to the filter form. If no valid filters exist in session or parsing fails, it initializes
   * the form with default values.
   *
   * Default behavior:
   * - If no date range exists in session: Sets min date to 7 days ago and max date to today
   * - If session data is corrupted: Logs an error, clears the storage, and continues with defaults
   * - Dispatches UpdateFilters action to synchronize the state with the loaded values
   *
   * @param activeTab - The currently active email delivery tab, used to determine which filters to load and which state action to dispatch
   *
   * @private
   */
  private _tryToFillFiltersFromSession(activeTab: EmailDeliveryTab): void {
    // Migration: copy legacy key to sent key on first access
    if (activeTab === EmailDeliveryTab.SENT) {
      const legacyFilters = this.keyValueStorage.get(SESSION_FILTERS_KEY);
      if (legacyFilters && !this.keyValueStorage.get(this.STORAGE_KEYS[EmailDeliveryTab.SENT])) {
        this.keyValueStorage.set(this.STORAGE_KEYS[EmailDeliveryTab.SENT], legacyFilters);
      }
    }

    const previousFilters = this.keyValueStorage.get(this.storageKey);

    let data: Partial<EmailDeliveryHubFilters> = {};

    try {
      data = (JSON.parse(previousFilters) ?? {}) as Partial<EmailDeliveryHubFilters>;
    } catch {
      this.logger.info(
        'EmailDeliveryHubFiltersComponent : tryToFillFiltersFromSession : Failed to fill filters with previous values from session, ignoring and cleaning...',
      );

      this.keyValueStorage.remove(this.storageKey);
    }

    // The `min`/`max` fields are moving into the `dateRange` field, so look at both during the transition period, which ends 04/07/2026.
    const dateRange: Range<string> = {
      min: data.dateRange?.min ?? data.min,
      max: data.dateRange?.max ?? data.max,
    }

    if (!dateRange.min && !dateRange.max) {
      const minDate = new Date();
      minDate.setDate(new Date().getDate() - DEFAULT_EMAIL_DELIVERY_HUB_DATE_RANGE);
      minDate.setHours(0, 0, 0, 0);

      dateRange.min = this.datePipe.transform(minDate, 'MM/dd/yyyy');
      dateRange.max = this.datePipe.transform(new Date(), 'MM/dd/yyyy');
    }

    const migratedTypes = this.isSentTab()
      ? this._dropUnavailableTypes(migrateLegacyEmailTypeFilters((data as EmailDeliveryHubSentFilters).types ?? [ALL_EMAIL_TYPES_LABEL]))
      : undefined;

    this.emailDeliveryHubFiltersGroup.patchValue(
      { ...data, min: dateRange.min, max: dateRange.max, ...(migratedTypes ? { types: migratedTypes } : {}) },
      { emitEvent: false },
    );

    if (!this.isSentTab()) {
      const showDismissedAndResolved = (data as EmailDeliveryHubUnsentFilters).showDismissedAndResolved ?? false;
      this.emailDeliveryHubFiltersGroup.get('showDismissedAndResolved').setValue(showDismissedAndResolved, { emitEvent: false });
    }

    if (!this.isSentTab() && !this._initializedTabs.has(activeTab)) {
      this._initializedTabs.add(activeTab);

      const unsentFilters = data as EmailDeliveryHubUnsentFilters;

      const filters: EmailDeliveryHubUnsentFilters = {
        dateRange,
        search: data.search ?? '',
        showDismissedAndResolved: unsentFilters.showDismissedAndResolved ?? false,
      };

      if (activeTab === EmailDeliveryTab.PENDING) {
        this.store.dispatch(new UpdatePendingFilters(filters));
      } else {
        this.store.dispatch(new UpdateFailedFilters(filters));
      }
    }

    if (this.isSentTab()) {
      const sentFilters = data as EmailDeliveryHubSentFilters;

      const filters: EmailDeliveryHubSentFilters = {
        dateRange,
        types: migratedTypes ?? [ALL_EMAIL_TYPES_LABEL],
        search: data.search ?? '',
        showBouncedOnly: sentFilters.showBouncedOnly ?? false,
      };

      // avoid redundant processing which can actually cause duplicate metric and row queries on page init
      if (this.store.selectSnapshot(EmailDeliveryHubState.getFiltersLoadable)?.status !== LoadableStatus.LOADED) {
        this.store.dispatch(new UpdateSentFilters(filters));
      }
    }
  }

  /**
   * Manages mutual exclusivity between "All Email Types" and individual type selections.
   *
   * This handler enforces the following rules:
   * - When individual types are selected alongside "All Email Types", removes "All Email Types" from the selection
   * - When all individual types are selected, resets the selection to only "All Email Types"
   * - When the selection is empty, resets to "All Email Types"
   * - Single selections (either "All Email Types" or a single type) are left unchanged
   *
   * @param data - Array of currently selected type values
   * @param previousValue - Array of previously selected type values (used to detect manual selection of "All Email Types")
   *
   * @example
   * // User selects "Invoice" while "All Email Types" is selected
   * // Result: ["Invoice"] (All Email Types removed)
   *
   * @example
   * // User selects all individual types
   * // Result: ["All Email Types"] (reset to all)
   *
   * @example
   * // User has only "Invoice" selected
   * // Result: ["Invoice"] (no change)
   */
  private _handleAllTypesAndIndividualToggle(data: string[], previousValue: string[] = []): void {
    /**
     *  Ignore the execution, in any scenario the length being one we don't need to
     *  process anything.
     *
     * Ex: All Email Types, Invoice or any other value. It is already correct, we don't need to update
     */
    if (data.length === 1) {
      return;
    }

    /**
     * Checking if it is needed to reset.
     */
    if (this._shouldResetToInitialValue(data, previousValue)) {
      return this.autoCompleteControl.setValue([ALL_EMAIL_TYPES_LABEL]);
    }

    /**
     * Exclude "All Email Types" if present
     */
    this.autoCompleteControl.setValue(data.filter((each) => each !== ALL_EMAIL_TYPES_LABEL));
  }

  /**
   * Determines if the email type selection should be reset to the initial "All Email Types" value.
   *
   * This method evaluates various conditions to decide whether the current selection state
   * warrants resetting to the default "All Email Types" option. It handles edge cases such as
   * empty selections, manual selection of "All Email Types", and scenarios where all individual
   * types have been selected.
   *
   * Reset conditions:
   * - The selection array is empty
   * - "All Email Types" was just added to the selection (not present in previous value)
   * - All individual email types are selected (effectively equivalent to "All Email Types")
   *
   * @param data - Array of currently selected email type values
   * @param previousValue - Array of previously selected values (defaults to empty array)
   * @returns true if the selection should be reset to ["All Email Types"], false otherwise
   *
   * @example
   * // Empty selection should reset
   * _shouldResetToInitialValue([], ['Invoice']) // returns true
   *
   * @example
   * // User manually selects "All Email Types" along with others
   * _shouldResetToInitialValue(['All Email Types', 'Invoice'], ['Invoice']) // returns true
   *
   * @example
   * // All individual types selected (should reset to "All")
   * _shouldResetToInitialValue(['Invoice', 'Smart Statement', 'Payment Confirmation', 'Payment Failure'], ['Invoice']) // returns true
   *
   * @example
   * // Normal individual selection (no reset needed)
   * _shouldResetToInitialValue(['Invoice', 'Smart Statement'], ['Invoice']) // returns false
   *
   * @private
   */
  /**
   * Drops cached type selections that are no longer available (e.g. a feature gate was turned off
   * between sessions). Falls back to "All Email Types" if every cached value is now unavailable.
   */
  private _dropUnavailableTypes(types: string[]): string[] {
    const availableOptions = new Set(this._autoCompleteEmailTypeOptions);
    const retained = types.filter((type) => availableOptions.has(type));
    return retained.length > 0 ? retained : [ALL_EMAIL_TYPES_LABEL];
  }

  private _shouldResetToInitialValue(data: string[], previousValue: string[] = []): boolean {
    if (data.length === 0) {
      return true;
    }

    const doesCurrentHasAllTypes = data.find((value) => value === ALL_EMAIL_TYPES_LABEL);
    const doesPreviousHasAllTypes = previousValue.find((value) => value === ALL_EMAIL_TYPES_LABEL);

    if (doesCurrentHasAllTypes && !doesPreviousHasAllTypes) {
      return true;
    }

    return areArraysEqual(data, this._autoCompleteEmailTypeOptions.slice(1), { ignoreOrder: true });
  }

  private formatSyncDateTime(epochMs: number): string {
    const date = new Date(epochMs);
    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const time = this.datePipe.transform(date, 'h:mm a');

    if (date >= today) {
      return time;
    }

    if (date >= yesterday) {
      return time + ' yesterday';
    }

    return this.datePipe.transform(date, 'M/d/yyyy h:mm a');
  }

  getDateRangePickerConfig(opens: TOpens): IDatePickerDirectiveConfig {
    return {
      ...DateConstants.DEFAULT_DATE_CONFIG,
      max: this.datePipe.transform(new Date(), 'MM/dd/yyyy'),
      opens,
    };
  }
}
